import { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, Play } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';

const SESSION_KEY = 'bvesuvio_intro_shown';
const SAFETY_MS   = 120000; // fallback estremo — chiude solo se onEnded/onError non scattano mai

export default function IntroVideo() {
  const { settings, loading } = useSiteSettings();
  const [visible,   setVisible]   = useState(false);
  const [closing,   setClosing]   = useState(false);
  const [showSkip,  setShowSkip]  = useState(false);
  const [muted,     setMuted]     = useState(true);   // parte muto per autoplay
  const [paused,    setPaused]    = useState(false);  // autoplay bloccato
  const videoRef   = useRef(null);
  const safetyRef  = useRef(null);
  const skipRef    = useRef(null);

  const rawUrl = settings?.intro_video_url || '';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  let videoUrl = rawUrl;
  if (videoUrl?.startsWith('/api/')) videoUrl = `${BACKEND_URL}${videoUrl}`;
  else if (videoUrl?.includes('localhost:'))
    videoUrl = videoUrl.replace(/https?:\/\/localhost:\d+/, BACKEND_URL);

  const dismiss = (markSeen = true) => {
    clearTimeout(skipRef.current);
    clearTimeout(safetyRef.current);
    if (markSeen) sessionStorage.setItem(SESSION_KEY, '1');
    setClosing(true);
    setTimeout(() => setVisible(false), 400);
  };

  const handlePlay = () => {
    // mostra "Salta" dopo 2s — nessun auto-dismiss: il video deve finire naturalmente
    clearTimeout(skipRef.current);
    skipRef.current = setTimeout(() => setShowSkip(true), 2000);
  };

  // Mostra intro al caricamento settings (una volta sola)
  useEffect(() => {
    if (loading || !videoUrl || sessionStorage.getItem(SESSION_KEY)) return;
    // Mai in modalità PWA (app salvata su home screen — sessionStorage non persiste)
    if (
      window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches
    ) return;
    setVisible(true);
    // timer di sicurezza estremo — solo se onEnded/onError non scattano mai
    safetyRef.current = setTimeout(dismiss, SAFETY_MS);
    return () => { clearTimeout(skipRef.current); clearTimeout(safetyRef.current); };
  }, [loading, videoUrl]); // eslint-disable-line

  // Avvia il video quando appare — muto per aggirare policy autoplay
  useEffect(() => {
    if (!visible || !videoRef.current) return;
    const vid = videoRef.current;
    vid.muted = true;
    const tryPlay = () =>
      vid.play()
        .then(() => setPaused(false))
        .catch(() => setPaused(true)); // autoplay bloccato → mostra pulsante play

    if (vid.readyState >= 2) tryPlay();
    else vid.addEventListener('canplay', tryPlay, { once: true });
  }, [visible]);

  // Sincronizza muted sul DOM element
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  // Avvio manuale se autoplay bloccato
  const handleManualPlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.muted = true;
    vid.play().then(() => { setPaused(false); setMuted(true); }).catch(() => {});
  };

  if (!visible) return null;

  return (
    <div className={`fixed inset-0 z-[9999] bg-black flex items-center justify-center transition-opacity duration-500 ${
      closing ? 'opacity-0 pointer-events-none' : 'opacity-100'
    }`}>
      {videoUrl && (
        <video
          ref={videoRef}
          key={videoUrl}
          src={videoUrl}
          autoPlay
          muted
          playsInline
          preload="auto"
          onPlay={handlePlay}
          onEnded={() => dismiss(true)}
          onError={() => dismiss(false)}
          className="absolute inset-0 w-full h-full object-contain"
        />
      )}

      {/* Gradiente basso */}
      <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

      {/* Logo */}
      <div className="absolute top-5 left-5 z-10">
        {settings?.logo_url
          ? <img src={settings.logo_url} alt="Logo" className="h-9 object-contain drop-shadow-lg" />
          : <span className="text-white font-semibold text-base drop-shadow-lg">Le Bontà del Vesuvio</span>
        }
      </div>

      {/* Pulsante PLAY manuale (solo se autoplay bloccato) */}
      {paused && (
        <button
          onClick={handleManualPlay}
          className="z-10 w-20 h-20 bg-white/20 hover:bg-white/35 backdrop-blur-sm border-2 border-white/50 rounded-full flex items-center justify-center transition-all"
        >
          <Play className="w-8 h-8 text-white fill-white ml-1" />
        </button>
      )}

      {/* Controlli in basso */}
      <div className="absolute bottom-5 inset-x-5 z-10 flex items-center justify-between">

        {/* 🔊 Unmute / Mute */}
        <button
          onClick={() => setMuted(m => !m)}
          className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/30 backdrop-blur-sm text-white rounded-full text-xs font-semibold border border-white/30 transition-all"
        >
          {muted
            ? <><VolumeX className="w-4 h-4" /> Attiva audio</>
            : <><Volume2 className="w-4 h-4" /> Audio attivo</>
          }
        </button>

        {/* Salta */}
        <button
          onClick={dismiss}
          className={`px-5 py-2 bg-white/15 hover:bg-white/30 backdrop-blur-sm text-white rounded-full text-sm font-semibold border border-white/30 transition-all duration-300 ${
            showSkip ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          Salta ›
        </button>
      </div>
    </div>
  );
}
